import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { motion, isValidMotionProp, AnimatePresence } from 'framer-motion';
import {
    Text,
    Box,
    Button,
    Flex,
    Portal,
    Tooltip,
    chakra,
    shouldForwardProp,
    Menu,
    MenuButton,
    MenuList
} from '@chakra-ui/react';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    CopyIcon,
    FolderIcon,
    MotionBox,
    TrashDockIcon
} from './UiElements';
import { useRef, useState } from 'react';
import layoutStore from '../stores/layoutStore';
import { tooltipAnimation } from './customTheme';
import { ReminderPanel, ReminderPopover } from './ReminderPopover';
import { commentBg } from '../Utils/commentBg';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { useCopyToClipboard } from '../Utils/useCopyToClipboard';
import useCustomToast from './ToastProvider';
import useStore from '../stores/store';
import useSelectStore from '../stores/selectStore';
import { MoveItemToAccordion } from './MoveAccordion';

const btnStyles = {
    bg: 'transparent',
    h: '36px',
    w: '36px',
    p: '0',
    alignItems: 'center',
    size: 'sm',
    rounded: '11px'
};

const SelectionBar = () => {
    const handleMouseDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <Flex
            onMouseDown={handleMouseDown}
            gap="4px"
            pos="relative"
            alignItems={'center'}
        >
            <SelectionCount />
            <MoveToMenu />
            <StackItemsButton />
            <DeleteButton />
        </Flex>
    );
};

export default SelectionBar;

const SelectionCount = () => {
    const selectedIds = useSelectStore((state) => state.selectedIds);

    return (
        <Tooltip
            label={'Selected items'}
            zIndex="500000"
            placement="top"
            rounded="6px"
            motionProps={tooltipAnimation}
        >
            <MotionBox
                mr="4px"
                initial={{ scale: 1.1 }}
                animate={{
                    scale: 1,
                    transition: { delay: 0.08, duration: 0.4, ease: 'easeOut' }
                }}
                key={selectedIds.length}
                w="36px"
                h="36px"
                alignItems="center"
                boxShadow={
                    'inset 0px 0px 0px 2px #44E18C, 0px 0px 6px 1px #44E18C30'
                }
                bg={'#44E18C14'}
                rounded={'11px'}
                cursor={'pointer'}
                display={'flex'}
                overflow={'hidden'}
            >
                <motion.div
                    key={selectedIds.length}
                    initial={{ scale: 1.325, color: '#44E18C' }}
                    animate={{
                        scale: 1,
                        color: 'var(--chakra-colors-chakra-body-text)',
                        transition: { color: { duration: 0.4 } }
                    }}
                    transition={{ duration: 0.2 }}
                    style={{
                        margin: 'auto'
                    }}
                >
                    <Text fontSize="16px" fontWeight="600" mr="1px">
                        {selectedIds.length}
                    </Text>
                </motion.div>
            </MotionBox>
        </Tooltip>
    );
};

const MoveToMenu = () => {
    const selectedIds = useSelectStore((state) => state.selectedIds);
    const menuBg = useColorModeValue('#FFF', '#262A2D');

    const [menuOpen, setMenu] = useState(false);
    const toggleMenu = () => {
        setMenu(!menuOpen);
    };
    const close = () => {
        setMenu(false);
    };

    return (
        <Menu isOpen={menuOpen} placement="top">
            <MenuButton
                as={Button}
                rightIcon={menuOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
                mx="4px"
                transition="0.12s"
                onClick={toggleMenu}
                rounded="9px"
                lineHeight={'1px'}
                h="38px"
            >
                Move to
            </MenuButton>
            <MenuList
                marginLeft="16px"
                fontSize="sm"
                p="4px"
                border="none"
                rounded="8px"
                bg={menuBg}
                boxShadow={'popoverShadowLight'}
                maxW="224px"
            >
                <MoveItemToAccordion
                    tabId={selectedIds} //TODO Pass down array of ids
                    closeSiteModal={close}
                />
            </MenuList>
        </Menu>
    );
};

const StackItemsButton = () => {
    const selectedIds = useSelectStore((state) => state.selectedIds);
    const anySelectedStack = useSelectStore(
        (state) => state.selectedExtraInfo.anySelectedStack
    );
    const selectedLength = selectedIds.length;
    const stackItems = () => {
        useStore.getState().stackSelectedItems();
    };
    const isOneSingleItem = selectedLength === 1;
    const isStacked =
        useSelectStore.getState().selectedExtraInfo?.anySelectedStack;
    const isDisabled = isOneSingleItem && !isStacked;

    return (
        <Tooltip
            label={
                (anySelectedStack ? 'Unstack ' : 'Stack ') +
                selectedLength + ' ' +
                (selectedLength === 1 ? 'item' : 'items')
            }
            zIndex="500000"
            placement="top"
            rounded="6px"
            motionProps={tooltipAnimation}
        >
            <Button
                {...btnStyles}
                onClick={stackItems}
                isDisabled={isDisabled}
                _disabled={{ opacity: 0.5 }}
            >
                <FolderIcon size={'20px'} />
            </Button>
        </Tooltip>
    );
};

const DeleteButton = () => {
    const deleteItems = () => {
        let idsToDelete = useSelectStore.getState().selectedIds;
        useStore.getState().deleteItems(idsToDelete);
    };

    return (
        <Tooltip
            label={'Delete items'}
            zIndex="500000"
            placement="top"
            rounded="6px"
            motionProps={tooltipAnimation}
        >
            <Button {...btnStyles} onClick={deleteItems}>
                <TrashDockIcon size={'20px'} />
            </Button>
        </Tooltip>
    );
};
